import moment from "moment";
import { Fragment, memo, useMemo } from "react";
import ChatMessage from "../../Components/ChatMessage/ChatMessage";
import { ConversationDateSeparator } from "../../Components/ConversationDateSeparator/ConversationDateSeparator";
import { isCaptureLogsNotice } from "../../Helper/CaptureRequest";
import { isRedundantAskForMoreHelp } from "../../Helper/AskForMoreHelp";
import { isChatWidgetAwaitingAnswer } from "../../Helper/ChatWidget";
import { findCaptureResultComment } from "../../Helper/CaptureResult";
import { EffectiveHoursConfig } from "../../Helper/EffectiveHoursConfig";
import { getMessageAuthorKey } from "../../Helper/MessageAuthor";
import { useConfigStore } from "../../Stores/Config";

type ConversationMessageListProps = {
  comments: any[] | null | undefined;
  currentBotAction: any;
  sessionGleapId?: string;
  scrollToBottom: () => void;
  joinVideoCall?: (meetingId: string) => void;
  actionsWithoutKai: string[];
  effectiveHoursConfig?: EffectiveHoursConfig;
};

const ConversationMessageListComponent = ({
  comments,
  currentBotAction,
  sessionGleapId,
  scrollToBottom,
  joinVideoCall,
  actionsWithoutKai,
  effectiveHoursConfig,
}: ConversationMessageListProps) => {
  const ticketHeaderMode = useConfigStore(
    (state) => state.config.ticketHeaderMode,
  );

  const visibleComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    // An answerbotflow prompt that carries text — e.g. the opening "Hey 👋 …how
    // can I help?" greeting — is rendered by BotActionInput while it's the
    // unanswered tail (the last comment). The moment the customer replies it's
    // no longer the tail, so show it here as a normal bot bubble: it then
    // persists in the timeline (above the customer's message) instead of
    // vanishing. Exactly one of the two renders it at any time, so no duplicate.
    // The diagnostic-logs notice is a system line appended whenever logs
    // arrive; it never makes the greeting stop being the unanswered tail.
    const tailComments = comments.filter(
      (comment) => !isCaptureLogsNotice(comment),
    );
    const lastCommentId =
      tailComments.length > 0
        ? tailComments[tailComments.length - 1].id
        : undefined;

    // The customer's message a capture card already shows (the screenshot or
    // recording it sent) folds into that card instead of repeating the file.
    const foldedIntoCard = new Set<any>();
    comments.forEach((comment) => {
      const requestId = comment.data?.captureRequest?.id;
      if (
        typeof requestId === "string" &&
        comment.data?.action?.type !== "capture" &&
        !comment.data?.hiddenAction
      ) {
        const result = findCaptureResultComment(comments, requestId);
        if (result) {
          foldedIntoCard.add(result);
        }
      }
    });

    return comments.filter((comment, index) => {
      if (foldedIntoCard.has(comment)) {
        return false;
      }
      // A capture request card (screenshot / screen recording) always stays in
      // the timeline — except the parked workflow `capture` step, which, like
      // every step, BotActionInput renders while it is the awaiting action.
      if (
        comment.data?.captureRequest &&
        comment.data?.action?.type !== "capture" &&
        !comment.data?.hiddenAction
      ) {
        return true;
      }
      // One of Kai's cards (show_widget) stays even without reply text.
      if (comment.data?.widget && !comment.data?.hiddenAction) {
        return true;
      }
      if (comment.type === "BOT" && comment.data?.action) {
        const isTail = comment.id === lastCommentId;
        const content = comment.data?.content;
        const hasText =
          typeof content === "string" ? content.trim().length > 0 : !!content;
        if (
          !isTail &&
          comment.data.action.type === "answerbotflow" &&
          hasText &&
          !isRedundantAskForMoreHelp(comments, index)
        ) {
          return true;
        }
        return false;
      }
      if (comment.data?.hiddenAction) {
        return false;
      }
      if (
        comment.type === "BOT" &&
        comment.data?.content?.length < 1 &&
        !comment.data?.showReplyTimes
      ) {
        return false;
      }

      return true;
    });
  }, [comments]);

  // Index of the single most-recent Kai answer that should carry the rating
  // prompt: the last real answer bubble (not a thinking/streaming placeholder).
  // Only this message renders the "Was this helpful?" thumbs — earlier answers
  // keep their attribution but stay thumb-free, so the prompt never repeats.
  const latestAnswerIndex = useMemo(() => {
    for (let i = visibleComments.length - 1; i >= 0; i--) {
      const data = visibleComments[i]?.data;
      // A capture card after the newest answer (Kai's or a teammate's ask,
      // which carries no answerBot flag) is the open turn: no thumbs on the
      // answer above it, where a rating would compete with the card.
      if (data?.captureRequest && !data?.answerBot) {
        return -1;
      }
      // A card after (or on) the newest answer still waits for the customer
      // (a form, a confirm…): there is nothing to rate yet.
      if (isChatWidgetAwaitingAnswer(data?.widget)) return -1;
      if (
        data?.answerBot &&
        !data?.isThinking &&
        !data?.isWritting &&
        !data?.captureLogsNotice
      ) {
        // Kai asking for a screenshot is not an answer to rate (kai-intake
        // CONTRACT §3.2): while the newest answer is a capture request, no
        // thumbs at all, not even on an earlier answer.
        return data?.captureRequest ? -1 : i;
      }
    }
    return -1;
  }, [visibleComments]);

  // Group boundaries for the whole list, computed in one pass. `firstOfGroup`
  // is derived from the PREVIOUS message's `lastOfGroup` rather than recomputed
  // against it, so the two can never disagree once the bot re-joins and the
  // tail suppression below have had their say.
  const groupFlags = useMemo(() => {
    const lastOfGroupFlags = visibleComments.map((comment, index) => {
      const nextComment = visibleComments[index + 1];
      let isLastInSection = false;

      if (!nextComment) {
        isLastInSection = true;
      }

      // A change of author ends the group. Without this, two agents replying
      // back to back merged into a single nameless block (#142998) — the old
      // checks here compared `comment.user`/`comment.session` by reference, so
      // they never matched on freshly deserialized comments.
      if (
        getMessageAuthorKey(comment, ticketHeaderMode) !==
        getMessageAuthorKey(nextComment ?? null, ticketHeaderMode)
      ) {
        isLastInSection = true;
      }

      if (comment.type !== nextComment?.type) {
        isLastInSection = true;
      }

      // The diagnostic-logs notice is a centred system line: it closes the
      // group above it and the next message opens a new one (with its header).
      if (isCaptureLogsNotice(comment) || isCaptureLogsNotice(nextComment)) {
        isLastInSection = true;
      }

      if (comment.type === "BOT" && nextComment?.type === "BOT_INPUT_REPLY") {
        isLastInSection = false;
      }

      if (
        comment.type === "BOT_INPUT_REPLY" &&
        nextComment?.type === "BOT_INPUT_REPLY"
      ) {
        isLastInSection = false;
      }

      const activeActionType = currentBotAction?.action?.type;

      // A rateable answer that is the current tail must KEEP its footer so the
      // "Was this helpful?" prompt can show on it — don't blank it out the way
      // we do for a plain in-progress bot action.
      const isRateableAnswerTail =
        !!comment.data?.answerBot &&
        activeActionType === "answerbotflow" &&
        (currentBotAction?.action?.allowRating ?? true);

      if (
        comment.type === "BOT" &&
        !nextComment &&
        visibleComments.length > 1 &&
        activeActionType &&
        !actionsWithoutKai.includes(activeActionType) &&
        !isRateableAnswerTail &&
        !isCaptureLogsNotice(comment)
      ) {
        isLastInSection = false;
      }

      return isLastInSection;
    });

    return visibleComments.map((comment, index) => {
      const nextComment = visibleComments[index + 1];
      const lastOfGroup = lastOfGroupFlags[index];
      const activeActionType = currentBotAction?.action?.type;

      const isRateableAnswerTail =
        !!comment.data?.answerBot &&
        activeActionType === "answerbotflow" &&
        (currentBotAction?.action?.allowRating ?? true);

      let showTime = true;

      if (
        comment.type === "BOT" &&
        !nextComment &&
        visibleComments.length > 1 &&
        activeActionType &&
        !actionsWithoutKai.includes(activeActionType) &&
        !isRateableAnswerTail
      ) {
        showTime = false;
      }

      if (nextComment && !lastOfGroup) {
        const duration = moment.duration(
          moment(nextComment.createdAt).diff(comment.createdAt),
        );
        if (duration.asMinutes() < 30) {
          showTime = false;
        }
      }

      return {
        firstOfGroup: index === 0 || lastOfGroupFlags[index - 1],
        lastOfGroup,
        showTime,
      };
    });
  }, [visibleComments, currentBotAction, actionsWithoutKai, ticketHeaderMode]);

  if (visibleComments.length === 0) {
    return null;
  }

  return (
    <>
      {visibleComments.map((comment, index) => {
        const { firstOfGroup, lastOfGroup, showTime } = groupFlags[index];

        let isCreator = false;
        if (comment.session?.gleapId === sessionGleapId) {
          isCreator = true;
        }

        const hasNextMessage = visibleComments.length > index + 1;

        // A day divider marks each crossing into a new calendar day; without it
        // a thread spanning weeks reads as one sitting, since the only other
        // time cue is a relative timestamp on the last message of a group.
        // Deliberately only BETWEEN messages: Conversation renders the ticket
        // body above this list, so a divider at index 0 would sit under it and
        // read as dating that message rather than opening the day.
        const previousComment = visibleComments[index - 1];
        const showDateSeparator =
          !!comment.createdAt &&
          !!previousComment?.createdAt &&
          !moment(previousComment.createdAt).isSame(comment.createdAt, "day");

        return (
          <Fragment key={comment.id}>
            {showDateSeparator && (
              <ConversationDateSeparator date={comment.createdAt} />
            )}
            <ChatMessage
              chatMessage={comment}
              showTime={showTime}
              isCreator={isCreator}
              firstOfGroup={firstOfGroup}
              lastOfGroup={lastOfGroup}
              imageLoaded={scrollToBottom}
              joinVideoCall={joinVideoCall}
              hasNextMessage={hasNextMessage}
              isLatestAnswer={index === latestAnswerIndex}
              effectiveHoursConfig={effectiveHoursConfig}
            />
          </Fragment>
        );
      })}
    </>
  );
};

export default memo(ConversationMessageListComponent);
